import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "../components/layout/AuthLayout.js";
import { Button } from "../components/ui/Button.js";
import { Field } from "../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { apiPost } from "../lib/api.js";
import { translateError } from "../lib/form.js";

const forgotSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "validation.required")
    .email("validation.email"),
});

type ForgotForm = z.infer<typeof forgotSchema>;

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotForm>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    // Always 204 / always show the confirmation (no account enumeration, §14.3).
    try {
      await apiPost<void>("/auth/forgot-password", { email: values.email });
    } catch {
      // Swallow: the confirmation text is identical either way.
    }
    setSent(true);
  });

  return (
    <AuthLayout
      title={t("auth.forgot.title")}
      subtitle={t("auth.forgot.subtitle")}
    >
      {sent ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-brand-soft px-4 py-3">
            <h2 className="text-sm font-semibold text-app-text">
              {t("auth.forgot.sentTitle")}
            </h2>
            <p className="mt-1 text-sm text-app-muted">
              {t("auth.forgot.sentMessage")}
            </p>
          </div>
          <Link
            to="/login"
            className="inline-block text-sm font-medium text-brand hover:underline"
          >
            {t("auth.forgot.backToLogin")}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <Field
            label={t("common.email")}
            htmlFor="email"
            error={translateError(errors.email?.message, t)}
            required
          >
            <Input
              id="email"
              type="email"
              autoComplete="email"
              invalid={!!errors.email}
              {...register("email")}
            />
          </Field>
          <Button
            type="submit"
            size="large"
            loading={isSubmitting || undefined}
            className="w-full"
          >
            {t("auth.forgot.submit")}
          </Button>
          <div>
            <Link
              to="/login"
              className="text-sm font-medium text-brand hover:underline"
            >
              {t("auth.forgot.backToLogin")}
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
